import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { API } from '@constants/api.constants';
import {
  Notification,
  NotificationsResponse,
  UnreadCountResponse
} from '@models/notifications.model';
import { Observable } from 'rxjs';
import { NotificationSoundService } from './notification-sound.service';

@Injectable({
  providedIn: 'root'
})
export class NotificationsService {
  private readonly http = inject(HttpClient);
  private readonly notificationSound = inject(NotificationSoundService);

  private readonly notifications = signal<Notification[]>([]);
  private readonly currentPage = signal<number>(1);
  private readonly hasNext = signal<boolean>(false);
  private readonly totalCount = signal<number>(0);

  readonly isLoading = signal<boolean>(false);
  readonly hasUnreadNotifications = signal<boolean>(false);
  readonly unreadNotificationsCount = signal<number>(0);

  /** When true, unread items show a short animated highlight when a new notification arrives. */
  hasNewSinceLastOpen = signal<boolean>(false);
  private newHighlightTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private static readonly NEW_HIGHLIGHT_DURATION_MS: number = 2000;
  /** Increment to trigger badge bounce animation once. */
  triggerBadgeBounce = signal<number>(0);
  /** Increment to trigger bell shake animation once. */
  triggerBellShake = signal<number>(0);

  getNotifications(page = 1): Observable<NotificationsResponse> {
    const params = new HttpParams().set('page', page.toString());
    return this.http.get<NotificationsResponse>(API.NOTIFICATIONS, { params });
  }

  getUnreadCount(): Observable<UnreadCountResponse> {
    return this.http.get<UnreadCountResponse>(API.NOTIFICATION_UNREAD_COUNT);
  }

  markAsRead(): Observable<void> {
    return this.http.post<void>(API.NOTIFICATION_MARK_AS_READ, {});
  }

  markSingleAsRead(uuid: string): Observable<void> {
    return this.http.post<void>(
      `${API.NOTIFICATION_MARK_SINGLE_READ}/${uuid}/read`,
      {}
    );
  }

  markSingleNotificationAsRead(uuid: string): void {
    if (!uuid) return;
    this.markSingleAsRead(uuid).subscribe({
      next: () => {
        this.notifications.update((list) =>
          list.map((n) => (n.uuid === uuid ? { ...n, is_read: true } : n))
        );
        const current = this.unreadNotificationsCount();
        if (current > 0) {
          const updated = current - 1;
          this.unreadNotificationsCount.set(updated);
          this.hasUnreadNotifications.set(updated > 0);
        }
      },
      error: (error) => {
        console.error('Error marking notification as read:', error);
      }
    });
  }

  loadNotifications(): void {
    if (!this.isLoading()) {
      return;
    }

    this.isLoading.set(true);
    this.currentPage.set(1);

    this.getNotifications(1).subscribe({
      next: (response) => {
        if (response.status === 'success' && response?.data) {
          this.notifications.set(response.data.list || []);
          this.hasNext.set(response.data.has_next || false);
          this.currentPage.set(response.data.page || 1);
          this.totalCount.set(response.data.total ?? 0);
        }
        this.isLoading.set(false);
      },
      error: (error) => {
        console.error('Error fetching notifications:', error);
        this.isLoading.set(false);
        this.notifications.set([]);
        this.hasNext.set(false);
      }
    });
  }

  loadMoreNotifications(): void {
    if (this.isLoading() || !this.hasNext()) {
      return;
    }

    this.isLoading.set(true);
    const nextPage = this.currentPage() + 1;

    this.getNotifications(nextPage).subscribe({
      next: (response) => {
        if (response.status === 'success' && response?.data) {
          this.notifications.update((current) => [
            ...current,
            ...(response.data.list || [])
          ]);
          this.hasNext.set(response.data.has_next || false);
          this.currentPage.set(response.data.page || 1);
        }
        this.isLoading.set(false);
      },
      error: (error) => {
        console.error('Error loading more notifications:', error);
        this.isLoading.set(false);
      }
    });
  }

  getNotificationsSignal() {
    return this.notifications.asReadonly();
  }

  getHasNextSignal() {
    return this.hasNext.asReadonly();
  }

  getTotalCountSignal() {
    return this.totalCount.asReadonly();
  }

  setLoadingState(loading: boolean) {
    this.isLoading.set(loading);
  }

  getUnreadNotificationsCount(): number {
    return this.unreadNotificationsCount();
  }

  loadUnreadCount(): void {
    this.getUnreadCount().subscribe({
      next: (response) => {
        if (response?.status === 'success' && response?.data) {
          const count = response.data.unread_count || 0;
          this.unreadNotificationsCount.set(count);
          this.hasUnreadNotifications.set(count > 0);
        } else {
          this.unreadNotificationsCount.set(0);
          this.hasUnreadNotifications.set(false);
        }
      },
      error: (error) => {
        console.error('Error fetching unread count:', error);
        this.unreadNotificationsCount.set(0);
        this.hasUnreadNotifications.set(false);
      }
    });
  }

  /**
   * Called when backend emits new_notification. Refetches count; if count increased,
   * triggers badge bounce, bell shake, toast; shows animated highlight on unread items for ~2s.
   */
  handleNewNotificationFromSocket(): void {
    const previousCount = this.unreadNotificationsCount();
    this.getUnreadCount().subscribe({
      next: (response) => {
        if (response?.status !== 'success' || !response?.data) {
          return;
        }
        const newCount = response.data.unread_count ?? 0;
        this.unreadNotificationsCount.set(newCount);
        this.hasUnreadNotifications.set(newCount > 0);
        if (newCount > previousCount) {
          this.notificationSound.play();
          this.triggerBadgeBounce.update((n) => n + 1);
          this.triggerBellShake.update((n) => n + 1);
          if (this.newHighlightTimeoutId !== null) {
            clearTimeout(this.newHighlightTimeoutId);
          }
          this.hasNewSinceLastOpen.set(true);
          this.newHighlightTimeoutId = setTimeout(() => {
            this.hasNewSinceLastOpen.set(false);
            this.newHighlightTimeoutId = null;
          }, NotificationsService.NEW_HIGHLIGHT_DURATION_MS);
        }
      },
      error: (error) => {
        console.error('Error fetching unread count:', error);
      }
    });
  }

  /** Clears "new" highlight (e.g. when user closes the notification panel). */
  clearNewHighlight(): void {
    if (this.newHighlightTimeoutId !== null) {
      clearTimeout(this.newHighlightTimeoutId);
      this.newHighlightTimeoutId = null;
    }
    this.hasNewSinceLastOpen.set(false);
  }

  markNotificationsAsRead(): void {
    this.markAsRead().subscribe({
      next: () => {
        this.notifications.update((list) =>
          list.map((n) => ({ ...n, is_read: true }))
        );
        this.unreadNotificationsCount.set(0);
        this.hasUnreadNotifications.set(false);
      },
      error: (error) => {
        console.error('Error marking notifications as read:', error);
      }
    });
  }
}
